import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell, ComposedChart, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import {
  Users, Clock, Target, TrendingUp, TrendingDown,
  ArrowUpRight, ArrowDownRight, Activity, Calendar,
  MessageCircle, DollarSign, AlertCircle, BarChart3 as PieChartIcon, 
  BarChart2, Flag, User, Star,
  Download, FileText, RefreshCw, Settings, Database, 
  ExternalLink, Loader, Building, Home, X, Check
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

// Professional Analytics Dashboard Component
const InKayrosProfessionalAnalytics = () => {
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [timeRange, setTimeRange] = useState('month');
  const [activeTab, setActiveTab] = useState('overview');
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Hooks de navegación y autenticación
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Configuración de colores
  const COLORS = {
    primary: '#2563EB',
    secondary: '#7C3AED', 
    success: '#059669',
    warning: '#D97706',
    danger: '#DC2626',
    info: '#0891B2',
    gray: '#6B7280',
    gradient: ['#2563EB', '#7C3AED', '#059669', '#D97706', '#DC2626', '#0891B2']
  };

  // Verificar permisos al cargar
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (user.tipo_usuario_id !== 5 && user.tipo_usuario_id !== 6) {
      setError('No tienes permisos para acceder a estas estadísticas');
      setTimeout(() => navigate('/admin/dashboard'), 3000);
      return;
    }
  }, [user, navigate]);

  // Función para hacer peticiones autenticadas
  const fetchWithAuth = async (endpoint) => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Token de autenticación no encontrado');
    }

    const response = await fetch(`https://backend-production-1786.up.railway.app${endpoint}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Error ${response.status}: ${response.statusText}`);
    }

    return response.json();
  };

  // Función principal para obtener datos de la API
  const fetchAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Verificar autenticación
      if (!user || (user.tipo_usuario_id !== 5 && user.tipo_usuario_id !== 6)) {
        throw new Error('No tienes permisos para acceder a estas estadísticas');
      }

      console.log('Obteniendo datos analíticos...');

      // Hacer llamadas paralelas a todos los endpoints individuales
      const [
        kpiData,
        timeSeriesData,
        geographicData,
        userSegmentationData,
        propertyPerformanceData,
        funnelData
      ] = await Promise.all([
        fetchWithAuth(`/api/analytics/kpi?timeRange=${timeRange}`).catch(err => {
          console.warn('Error en KPI:', err);
          return null;
        }),
        fetchWithAuth(`/api/analytics/time-series?timeRange=${timeRange}&days=30`).catch(err => {
          console.warn('Error en Time Series:', err);
          return null;
        }),
        fetchWithAuth('/api/analytics/geographic').catch(err => {
          console.warn('Error en Geographic:', err);
          return null;
        }),
        fetchWithAuth('/api/analytics/user-segmentation').catch(err => {
          console.warn('Error en User Segmentation:', err);
          return null;
        }),
        fetchWithAuth('/api/analytics/property-performance').catch(err => {
          console.warn('Error en Property Performance:', err);
          return null;
        }),
        fetchWithAuth(`/api/analytics/funnel?timeRange=${timeRange}`).catch(err => {
          console.warn('Error en Funnel:', err);
          return null;
        })
      ]);

      // Consolidar datos
      const consolidatedData = {
        kpi: kpiData || {},
        timeSeries: timeSeriesData || [],
        geographic: geographicData || [],
        userSegmentation: userSegmentationData || [],
        propertyPerformance: propertyPerformanceData || [],
        funnel: funnelData || [],
        timestamp: new Date().toISOString(),
        isMockData: false
      };

      // Si no hay datos suficientes, usar datos mock
      if (!kpiData && !timeSeriesData && !geographicData) {
        console.log('No hay datos suficientes de la API, usando datos de ejemplo...');
        setStats(generateMockData());
      } else {
        console.log('Datos recibidos exitosamente:', consolidatedData);
        setStats(consolidatedData);
      }
      
      setLastUpdate(new Date());

    } catch (error) {
      console.error('Error al cargar datos analíticos:', error);
      
      // Manejar diferentes tipos de error
      if (error.message.includes('401') || error.message.includes('Token')) {
        setError('Sesión expirada. Redirigiendo al login...');
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      } else if (error.message.includes('403')) {
        setError('No tienes permisos para acceder a estas estadísticas');
      } else if (error.message.includes('5')) {
        setError('Error del servidor. Intenta de nuevo más tarde.');
      } else {
        setError(error.message || 'Error al cargar datos');
      }
      
      // Usar datos mock como fallback
      console.log('Usando datos de ejemplo como fallback...');
      setStats(generateMockData());
    } finally {
      setLoading(false);
    }
  };

  // Función para generar datos mock como fallback
  const generateMockData = () => {
    const baseMultiplier = timeRange === 'week' ? 1 : timeRange === 'month' ? 4 : 12;
    
    const kpiMetrics = {
      totalUsers: 2847 * baseMultiplier,
      activeUsers: 2234 * baseMultiplier,
      newUsersToday: 23,
      userGrowthRate: 12.5,
      userRetentionRate: 78.3,
      totalProperties: 1582 * baseMultiplier,
      activeProperties: 1421 * baseMultiplier,
      newPropertiesToday: 8,
      propertyGrowthRate: 15.2,
      averagePropertyValue: 285000,
      totalRevenue: 145000 * baseMultiplier,
      monthlyRecurringRevenue: 28500,
      averageRevenuePerUser: 125.50,
      revenueGrowthRate: 18.7,
      totalViews: 15643 * baseMultiplier,
      totalClicks: 3421 * baseMultiplier,
      totalMessages: 1876 * baseMultiplier,
      conversionRate: 11.8,
      clickThroughRate: 21.9,
      totalReports: 87 * baseMultiplier,
      resolvedReports: 72 * baseMultiplier,
      averageResolutionTime: 2.3,
      customerSatisfaction: 4.6
    };

    const timeSeriesData = Array.from({ length: 30 }, (_, i) => ({
      date: new Date(2025, 5, i + 1).toISOString().split('T')[0],
      users: Math.floor(Math.random() * 150) + 200,
      properties: Math.floor(Math.random() * 50) + 80,
      revenue: Math.floor(Math.random() * 5000) + 8000,
      views: Math.floor(Math.random() * 500) + 800,
      conversions: Math.floor(Math.random() * 30) + 50,
      reports: Math.floor(Math.random() * 5) + 2
    }));

    const geographicData = [
      { region: 'Lima', users: 1285, properties: 754, percentage: 45.2 },
      { region: 'Arequipa', users: 456, properties: 287, percentage: 16.0 },
      { region: 'Cusco', users: 287, properties: 198, percentage: 10.1 },
      { region: 'Trujillo', users: 198, properties: 156, percentage: 7.0 },
      { region: 'Piura', users: 165, properties: 98, percentage: 5.8 },
      { region: 'Chiclayo', users: 142, properties: 87, percentage: 5.0 },
      { region: 'Otros', users: 314, properties: 256, percentage: 10.9 }
    ];

    const userSegmentation = [
      { segment: 'Premium Users', count: 287, revenue: 45600, growth: 23.5 },
      { segment: 'Basic Users', count: 892, revenue: 17800, growth: 15.2 },
      { segment: 'Free Users', count: 1668, revenue: 0, growth: 8.7 }
    ];

    const propertyPerformance = [
      { type: 'Casa', avg_views: 245, avg_clicks: 58, avg_messages: 23, conversion: 12.8 },
      { type: 'Departamento', avg_views: 189, avg_clicks: 45, avg_messages: 19, conversion: 14.2 },
      { type: 'Terreno', avg_views: 98, avg_clicks: 18, avg_messages: 7, conversion: 8.1 },
      { type: 'Local Comercial', avg_views: 156, avg_clicks: 32, avg_messages: 12, conversion: 9.7 },
      { type: 'Oficina', avg_views: 87, avg_clicks: 21, avg_messages: 9, conversion: 11.5 }
    ];

    const funnelData = [
      { stage: 'Visitas', value: 15643, percentage: 100 },
      { stage: 'Clics en Compartir', value: 3421, percentage: 21.9 },
      { stage: 'Mensajes Enviados', value: 1876, percentage: 12.0 },
      { stage: 'Contactos Realizados', value: 897, percentage: 5.7 },
      { stage: 'Ventas/Alquileres', value: 234, percentage: 1.5 }
    ];

    return {
      kpi: kpiMetrics,
      timeSeries: timeSeriesData,
      geographic: geographicData,
      userSegmentation: userSegmentation,
      propertyPerformance: propertyPerformance,
      funnel: funnelData,
      isMockData: true
    };
  };

  // Cargar datos al inicio y cuando cambie timeRange
  useEffect(() => {
    if (user && (user.tipo_usuario_id === 5 || user.tipo_usuario_id === 6)) {
      fetchAnalyticsData();
    }
  }, [timeRange, user]);

  // Auto-refresh cada 5 minutos
  useEffect(() => {
    if (!stats || error) return;

    const interval = setInterval(() => {
      if (activeTab === 'overview' && !loading) {
        fetchAnalyticsData();
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [activeTab, loading, stats, error]);

  // Función de refresh manual
  const refreshData = async () => {
    await fetchAnalyticsData();
  };

  // Funciones auxiliares para formatear números de forma segura
  const safeNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || isNaN(value)) {
      return defaultValue;
    }
    return Number(value);
  };

  const safeToLocaleString = (value, defaultValue = 0) => {
    return safeNumber(value, defaultValue).toLocaleString();
  };

  const safePercentage = (value, defaultValue = 0) => {
    return `${safeNumber(value, defaultValue)}%`;
  };

  // Función para asegurar que un valor sea un array
  const ensureArray = (value) => {
    if (Array.isArray(value)) {
      return value;
    }
    return [];
  };

  // Función para obtener datos de forma segura
  const getSafeData = () => {
    if (!stats) return null;
    
    return {
      kpi: stats.kpi || {},
      timeSeries: ensureArray(stats.timeSeries),
      geographic: ensureArray(stats.geographic),
      userSegmentation: ensureArray(stats.userSegmentation),
      propertyPerformance: ensureArray(stats.propertyPerformance),
      funnel: ensureArray(stats.funnel),
      isMockData: stats.isMockData || false
    };
  };

  // Funciones de exportación
  const exportToExcel = () => {
    if (!stats) {
      alert('No hay datos para exportar');
      return;
    }

    const csvContent = generateComprehensiveCSV();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inkayros-analytics-${activeTab}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generateComprehensiveCSV = () => {
    if (!stats) return '';
    
    let csv = 'InKayros Analytics Professional Report\n';
    csv += `Generated on: ${new Date().toLocaleDateString()}\n`;
    csv += `Time Range: ${timeRange}\n`;
    csv += `Active Tab: ${activeTab}\n`;
    csv += `Data Source: ${stats.isMockData ? 'Mock Data (Fallback)' : 'Real API Data'}\n`;
    if (lastUpdate) csv += `Last Update: ${lastUpdate.toLocaleString()}\n`;
    csv += '\n';
    
    // Executive Summary
    csv += 'EXECUTIVE SUMMARY\n';
    csv += 'Metric,Value,Growth Rate\n';
    csv += `Total Users,${safeToLocaleString(stats.kpi.totalUsers)},${stats.kpi.userGrowthRate}%\n`;
    csv += `Active Users,${safeToLocaleString(stats.kpi.activeUsers)},${stats.kpi.userGrowthRate}%\n`;
    csv += `Total Properties,${safeToLocaleString(stats.kpi.totalProperties)},${stats.kpi.propertyGrowthRate}%\n`;
    csv += `Total Revenue,${safeToLocaleString(stats.kpi.totalRevenue)},${stats.kpi.revenueGrowthRate}%\n`;
    csv += `Conversion Rate,${stats.kpi.conversionRate}%,2.3%\n`;
    csv += `Customer Satisfaction,${stats.kpi.customerSatisfaction}/5,8.1%\n\n`;
    
    return csv;
  };

  const exportToPDF = () => {
    const printWindow = window.open('', '_blank');
    const printContent = generatePrintContent();
    
    printWindow.document.write(`
      <html>
        <head>
          <title>InKayros Analytics Report - ${activeTab}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin-bottom: 30px; }
            .kpi-card { border: 1px solid #ddd; padding: 15px; border-radius: 8px; }
            .kpi-title { font-size: 12px; color: #666; margin-bottom: 5px; }
            .kpi-value { font-size: 24px; font-weight: bold; }
            .kpi-trend { font-size: 12px; color: #059669; }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  const generatePrintContent = () => {
    const safeStats = getSafeData();
    
    return `
      <div class="header">
        <h1>InKayros Analytics Professional Report</h1>
        <p>Generated on ${new Date().toLocaleDateString()} | Time Range: ${timeRange}</p>
      </div>
      
      <div class="kpi-grid">
        <div class="kpi-card">
          <div class="kpi-title">Active Users</div>
          <div class="kpi-value">${safeToLocaleString(safeStats?.kpi?.activeUsers)}</div>
          <div class="kpi-trend">+${safeNumber(safeStats?.kpi?.userGrowthRate)}%</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-title">Total Properties</div>
          <div class="kpi-value">${safeToLocaleString(safeStats?.kpi?.totalProperties)}</div>
          <div class="kpi-trend">+${safeNumber(safeStats?.kpi?.propertyGrowthRate)}%</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-title">Total Revenue</div>
          <div class="kpi-value">${safeToLocaleString(safeStats?.kpi?.totalRevenue)}</div>
          <div class="kpi-trend">+${safeNumber(safeStats?.kpi?.revenueGrowthRate)}%</div>
        </div>
        <div class="kpi-card">
          <div class="kpi-title">Conversion Rate</div>
          <div class="kpi-value">${safeNumber(safeStats?.kpi?.conversionRate)}%</div>
          <div class="kpi-trend">+2.3%</div>
        </div>
      </div>
    `;
  };

  // Componentes auxiliares
  const KPICard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-lg bg-${color}-50`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center text-sm font-medium ${
            trend > 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {trend > 0 ? <ArrowUpRight className="w-4 h-4 mr-1" /> : <ArrowDownRight className="w-4 h-4 mr-1" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div>
        <h3 className="text-sm font-medium text-gray-600 mb-1">{title}</h3>
        <p className="text-2xl font-bold text-gray-900 mb-1">{value}</p>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
    </div>
  );

  const ChartContainer = ({ title, children, className = "", actions }) => (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {actions && <div className="flex items-center space-x-2">{actions}</div>}
      </div>
      <div className="h-80">
        {children}
      </div>
    </div>
  );

  const MetricTable = ({ title, data, columns }) => {
    const safeData = ensureArray(data);
    
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                {columns.map((col, index) => (
                  <th key={index} className="py-3 px-4 text-left text-sm font-medium text-gray-600">
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {safeData.length > 0 ? (
                safeData.map((row, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    {columns.map((col, colIndex) => (
                      <td key={colIndex} className="py-3 px-4 text-sm text-gray-900">
                        {col.render ? col.render(row[col.key], row) : (row[col.key] || 'N/A')}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="py-8 px-4 text-center text-gray-500">
                    No hay datos disponibles
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Estado de error
  if (error && !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error al cargar datos</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={refreshData}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // Estado de carga
  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando dashboard analítico...</p>
          <p className="text-sm text-gray-500 mt-2">Obteniendo datos en tiempo real...</p>
        </div>
      </div>
    );
  }

  // Verificar que los datos estén disponibles antes de renderizar
  if (!stats || !stats.kpi) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando dashboard analítico...</p>
          <p className="text-sm text-gray-500 mt-2">Verificando datos...</p>
        </div>
      </div>
    );
  }

  // Obtener datos seguros
  const safeStats = getSafeData();

  // Renderizado principal
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Database className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">InKayros Analytics</h1>
                <div className="flex items-center space-x-2">
                  <p className="text-sm text-gray-500">Dashboard Profesional de Datos</p>
                  {!safeStats?.isMockData && lastUpdate && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <div className="w-2 h-2 bg-green-400 rounded-full mr-1 animate-pulse"></div>
                      Datos en vivo
                    </span>
                  )}
                  {safeStats?.isMockData && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Datos de ejemplo
                    </span>
                  )}
                  {error && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      <X className="w-3 h-3 mr-1" />
                      Error de conexión
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Time Range Selector */}
              <select 
                value={timeRange} 
                onChange={(e) => setTimeRange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loading}
              >
                <option value="week">Última Semana</option>
                <option value="month">Último Mes</option>
                <option value="quarter">Último Trimestre</option>
                <option value="year">Último Año</option>
              </select>
              
              {/* Refresh Button */}
              <button
                onClick={refreshData}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Cargando...' : 'Actualizar'}
              </button>
              
              {/* Export Buttons */}
              <button
                onClick={exportToExcel}
                disabled={!stats}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4 mr-2" />
                Excel
              </button>
              
              <button
                onClick={exportToPDF}
                disabled={!stats}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </button>
              
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Resumen Ejecutivo', icon: PieChartIcon },
              { id: 'users', label: 'Análisis de Usuarios', icon: Users },
              { id: 'properties', label: 'Rendimiento de Propiedades', icon: Building },
              { id: 'revenue', label: 'Ingresos y Finanzas', icon: DollarSign },
              { id: 'reports', label: 'Reportes y Soporte', icon: Flag }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Last Update Info */}
        {lastUpdate && !safeStats?.isMockData && (
          <div className="mb-6 text-sm text-gray-500 text-center">
            Última actualización: {lastUpdate.toLocaleString()}
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Executive KPIs */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Indicadores Clave de Rendimiento</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <KPICard
                  title="Usuarios Activos"
                  value={safeToLocaleString(stats?.kpi?.activeUsers)}
                  subtitle={`${safeNumber(stats?.kpi?.newUsersToday)} nuevos hoy`}
                  icon={Users}
                  trend={safeNumber(stats?.kpi?.userGrowthRate)}
                  color="blue"
                />
                <KPICard
                  title="Propiedades Activas"
                  value={safeToLocaleString(stats?.kpi?.activeProperties)}
                  subtitle={`${safeNumber(stats?.kpi?.newPropertiesToday)} nuevas hoy`}
                  icon={Building}
                  trend={safeNumber(stats?.kpi?.propertyGrowthRate)}
                  color="purple"
                />
                <KPICard
                  title="Ingresos Totales"
                  value={`${(safeNumber(stats?.kpi?.totalRevenue) / 1000).toFixed(1)}K`}
                  subtitle={`MRR: ${(safeNumber(stats?.kpi?.monthlyRecurringRevenue) / 1000).toFixed(1)}K`}
                  icon={DollarSign}
                  trend={safeNumber(stats?.kpi?.revenueGrowthRate)}
                  color="green"
                />
                <KPICard
                  title="Tasa de Conversión"
                  value={safePercentage(stats?.kpi?.conversionRate)}
                  subtitle={`CTR: ${safePercentage(stats?.kpi?.clickThroughRate)}`}
                  icon={TrendingUp}
                  trend={2.3}
                  color="indigo"
                />
              </div>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Growth Trends */}
              <ChartContainer title="Tendencias de Crecimiento">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={stats.timeSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Legend />
                    <Area 
                      yAxisId="left"
                      type="monotone" 
                      dataKey="users" 
                      fill="#dbeafe" 
                      stroke="#3b82f6" 
                      name="Usuarios"
                    />
                    <Bar yAxisId="right" dataKey="properties" fill="#8b5cf6" name="Propiedades" />
                    <Line 
                      yAxisId="right"
                      type="monotone" 
                      dataKey="conversions" 
                      stroke="#059669" 
                      strokeWidth={3}
                      name="Conversiones"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartContainer>

              {/* Geographic Distribution */}
              <ChartContainer title="Distribución Geográfica">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.geographic}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={2}
                      dataKey="users"
                      label={({ region, percentage }) => `${region} (${percentage}%)`}
                    >
                      {safeStats.geographic.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS.gradient[index % COLORS.gradient.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [value.toLocaleString(), "Usuarios"]} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartContainer>
            </div>

            {/* Conversion Funnel */}
            <ChartContainer title="Embudo de Conversión" className="lg:col-span-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="horizontal" data={stats.funnel}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="stage" type="category" width={150} tick={{ fontSize: 12 }} />
                  <Tooltip 
                    formatter={(value, name) => [value.toLocaleString(), name]}
                    labelFormatter={(label) => `Etapa: ${label}`}
                  />
                  <Bar dataKey="value" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                    {safeStats.funnel.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS.gradient[index % COLORS.gradient.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartContainer>

            {/* Performance Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <MetricTable
                title="Rendimiento por Tipo de Propiedad"
                data={stats.propertyPerformance}
                columns={[
                  { key: 'type', header: 'Tipo de Propiedad' },
                  { key: 'avg_views', header: 'Vistas Promedio', render: (val) => val.toLocaleString() },
                  { key: 'avg_clicks', header: 'Clics Promedio', render: (val) => val.toLocaleString() },
                  { key: 'conversion', header: 'Conversión %', render: (val) => `${val}%` }
                ]}
              />
              
              <MetricTable
                title="Segmentación de Usuarios"
                data={stats.userSegmentation}
                columns={[
                  { key: 'segment', header: 'Segmento' },
                  { key: 'count', header: 'Usuarios', render: (val) => val.toLocaleString() },
                  { key: 'revenue', header: 'Ingresos', render: (val) => `$${val.toLocaleString()}` },
                  { key: 'growth', header: 'Crecimiento %', render: (val) => `${val}%` }
                ]}
              />
            </div>
          </div>
        )}

        {/* Users Analytics Tab */}
        {activeTab === 'users' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Análisis Detallado de Usuarios</h2>
              
              {/* User KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                <KPICard
                  title="Usuarios Totales"
                  value={safeToLocaleString(stats?.kpi?.totalUsers)}
                  subtitle="Registrados"
                  icon={Users}
                  trend={12.5}
                  color="blue"
                />
                <KPICard
                  title="Usuarios Activos"
                  value={safeToLocaleString(stats?.kpi?.activeUsers)}
                  subtitle="Último mes"
                  icon={Activity}
                  trend={8.3}
                  color="green"
                />
                <KPICard
                  title="Retención"
                  value={safePercentage(stats?.kpi?.userRetentionRate)}
                  subtitle="30 días"
                  icon={Target}
                  trend={2.1}
                  color="purple"
                />
                <KPICard
                  title="Nuevos Hoy"
                  value={safeNumber(stats?.kpi?.newUsersToday).toString()}
                  subtitle="Registros"
                  icon={TrendingUp}
                  trend={15.2}
                  color="indigo"
                />
                <KPICard
                  title="ARPU"
                  value={`$${safeNumber(stats?.kpi?.averageRevenuePerUser)}`}
                  subtitle="Promedio/usuario"
                  icon={DollarSign}
                  trend={5.7}
                  color="emerald"
                />
              </div>

              {/* User Activity Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <ChartContainer title="Registro de Usuarios por Día">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={stats?.timeSeries || []}>
                      <defs>
                        <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Area 
                        type="monotone" 
                        dataKey="users" 
                        stroke="#3b82f6" 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#colorUsers)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartContainer>

                <ChartContainer title="Segmentación por Plan">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats?.userSegmentation || []}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ segment, count }) => `${segment || 'N/A'}: ${safeNumber(count)}`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {(stats?.userSegmentation || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS.gradient[index % COLORS.gradient.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>

              {/* Detailed User Table */}
              <MetricTable
                title="Análisis de Cohortes por Segmento"
                data={stats?.userSegmentation || []}
                columns={[
                  { key: 'segment', header: 'Segmento de Usuario' },
                  { key: 'count', header: 'Total Usuarios', render: (val) => safeToLocaleString(val) },
                  { key: 'revenue', header: 'Ingresos Generados', render: (val) => `$${safeToLocaleString(val)}` },
                  { key: 'growth', header: 'Crecimiento %', render: (val) => (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      safeNumber(val) > 15 ? 'bg-green-100 text-green-800' : 
                      safeNumber(val) > 10 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {safePercentage(val)}
                    </span>
                  )},
                  { 
                    key: 'revenue', 
                    header: 'ARPU', 
                    render: (val, row) => `$${(safeNumber(val) / Math.max(safeNumber(row.count), 1)).toFixed(2)}` 
                  }
                ]}
              />
            </div>
          </div>
        )}

        {/* Properties Analytics Tab */}
        {activeTab === 'properties' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Análisis de Rendimiento de Propiedades</h2>
              
              {/* Property KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <KPICard
                  title="Propiedades Totales"
                  value={safeToLocaleString(stats?.kpi?.totalProperties)}
                  subtitle="Publicadas"
                  icon={Building}
                  trend={15.2}
                  color="blue"
                />
                <KPICard
                  title="Propiedades Activas"
                  value={safeToLocaleString(stats?.kpi?.activeProperties)}
                  subtitle="Disponibles"
                  icon={Home}
                  trend={8.7}
                  color="green"
                />
                <KPICard
                  title="Valor Promedio"
                  value={`$${(safeNumber(stats?.kpi?.averagePropertyValue) / 1000).toFixed(0)}K`}
                  subtitle="Por propiedad"
                  icon={DollarSign}
                  trend={3.2}
                  color="purple"
                />
                <KPICard
                  title="Tiempo Promedio"
                  value="45 días"
                  subtitle="En mercado"
                  icon={Clock}
                  trend={-5.8}
                  color="orange"
                />
              </div>

              {/* Property Performance Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <ChartContainer title="Rendimiento por Tipo de Propiedad">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats?.propertyPerformance || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="type" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="avg_views" fill="#3b82f6" name="Vistas Promedio" />
                      <Bar dataKey="avg_clicks" fill="#8b5cf6" name="Clics Promedio" />
                      <Bar dataKey="avg_messages" fill="#059669" name="Mensajes Promedio" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>

                <ChartContainer title="Tasa de Conversión por Tipo">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={stats?.propertyPerformance || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="type" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value) => [`${safeNumber(value)}%`, 'Conversión']} />
                      <Line 
                        type="monotone" 
                        dataKey="conversion" 
                        stroke="#059669" 
                        strokeWidth={3}
                        dot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>

              {/* Property Performance Table */}
              <MetricTable
                title="Métricas Detalladas por Tipo de Propiedad"
                data={stats?.propertyPerformance || []}
                columns={[
                  { key: 'type', header: 'Tipo de Propiedad' },
                  { key: 'avg_views', header: 'Vistas Promedio', render: (val) => safeToLocaleString(val) },
                  { key: 'avg_clicks', header: 'Clics Promedio', render: (val) => safeToLocaleString(val) },
                  { key: 'avg_messages', header: 'Mensajes Promedio', render: (val) => safeToLocaleString(val) },
                  { 
                    key: 'conversion', 
                    header: 'Tasa de Conversión', 
                    render: (val) => (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        safeNumber(val) > 12 ? 'bg-green-100 text-green-800' : 
                        safeNumber(val) > 10 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {safePercentage(val)}
                      </span>
                    )
                  }
                ]}
              />
            </div>
          </div>
        )}

        {/* Revenue Analytics Tab */}
        {activeTab === 'revenue' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Análisis Financiero y de Ingresos</h2>
              
              {/* Revenue KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <KPICard
                  title="Ingresos Totales"
                  value={`$${(safeNumber(stats?.kpi?.totalRevenue) / 1000).toFixed(1)}K`}
                  subtitle="Este período"
                  icon={DollarSign}
                  trend={18.7}
                  color="green"
                />
                <KPICard
                  title="MRR"
                  value={`$${(safeNumber(stats?.kpi?.monthlyRecurringRevenue) / 1000).toFixed(1)}K`}
                  subtitle="Ingresos recurrentes"
                  icon={TrendingUp}
                  trend={12.3}
                  color="blue"
                />
                <KPICard
                  title="ARPU"
                  value={`$${safeNumber(stats?.kpi?.averageRevenuePerUser)}`}
                  subtitle="Por usuario"
                  icon={User}
                  trend={5.7}
                  color="purple"
                />
                <KPICard
                  title="Crecimiento"
                  value={safePercentage(stats?.kpi?.revenueGrowthRate)}
                  subtitle="Mes a mes"
                  icon={BarChart2}
                  trend={18.7}
                  color="emerald"
                />
              </div>

              {/* Revenue Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <ChartContainer title="Tendencia de Ingresos">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={stats?.timeSeries || []}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value) => [`$${safeToLocaleString(value)}`, 'Ingresos']} />
                      <Area 
                        type="monotone" 
                        dataKey="revenue" 
                        stroke="#059669" 
                        strokeWidth={2}
                        fillOpacity={1} 
                        fill="url(#colorRevenue)" 
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartContainer>

                <ChartContainer title="Ingresos por Segmento">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats?.userSegmentation || []}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ segment, revenue }) => `${segment || 'N/A'}: $${safeToLocaleString(revenue)}`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="revenue"
                      >
                        {(stats?.userSegmentation || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS.gradient[index % COLORS.gradient.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [`$${safeToLocaleString(value)}`, 'Ingresos']} />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>

              {/* Revenue Table */}
              <MetricTable
                title="Análisis de Ingresos por Segmento"
                data={stats?.userSegmentation || []}
                columns={[
                  { key: 'segment', header: 'Segmento' },
                  { key: 'count', header: 'Usuarios', render: (val) => safeToLocaleString(val) },
                  { key: 'revenue', header: 'Ingresos Totales', render: (val) => `$${safeToLocaleString(val)}` },
                  { 
                    key: 'revenue', 
                    header: 'ARPU', 
                    render: (val, row) => `$${(safeNumber(val) / Math.max(safeNumber(row.count), 1)).toFixed(2)}` 
                  },
                  { 
                    key: 'growth', 
                    header: 'Crecimiento', 
                    render: (val) => (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        safeNumber(val) > 20 ? 'bg-green-100 text-green-800' : 
                        safeNumber(val) > 15 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {safePercentage(val)}
                      </span>
                    )
                  }
                ]}
              />
            </div>
          </div>
        )}

        {/* Reports Analytics Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Gestión de Reportes y Soporte</h2>
              
              {/* Reports KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <KPICard
                  title="Reportes Totales"
                  value={safeNumber(safeStats?.kpi?.totalReports).toString()}
                  subtitle="Este período"
                  icon={Flag}
                  trend={-8.2}
                  color="red"
                />
                <KPICard
                  title="Reportes Resueltos"
                  value={safeNumber(safeStats?.kpi?.resolvedReports).toString()}
                  subtitle="Completados"
                  icon={Check}
                  trend={15.3}
                  color="green"
                />
                <KPICard
                  title="Tiempo Promedio"
                  value={`${safeNumber(safeStats?.kpi?.averageResolutionTime)} días`}
                  subtitle="Resolución"
                  icon={Clock}
                  trend={-12.8}
                  color="blue"
                />
                <KPICard
                  title="Satisfacción"
                  value={`${safeNumber(safeStats?.kpi?.customerSatisfaction)}/5`}
                  subtitle="Calificación"
                  icon={Star}
                  trend={8.1}
                  color="yellow"
                />
              </div>

              {/* Reports Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                <ChartContainer title="Tendencia de Reportes">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={safeStats.timeSeries}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="reports" 
                        fill="#fecaca" 
                        stroke="#dc2626" 
                        name="Reportes Recibidos"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="reports" 
                        stroke="#059669" 
                        strokeWidth={2}
                        name="Reportes Resueltos"
                        dot={{ r: 4 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ChartContainer>

                <ChartContainer title="Distribución de Estados">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Resueltos', value: safeNumber(safeStats?.kpi?.resolvedReports), color: '#059669' },
                          { name: 'Pendientes', value: safeNumber(safeStats?.kpi?.totalReports) - safeNumber(safeStats?.kpi?.resolvedReports), color: '#d97706' },
                          { name: 'En Proceso', value: Math.floor(safeNumber(safeStats?.kpi?.totalReports) * 0.1), color: '#3b82f6' }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${safeNumber(value)}`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {[
                          { name: 'Resueltos', value: safeNumber(safeStats?.kpi?.resolvedReports), color: '#059669' },
                          { name: 'Pendientes', value: safeNumber(safeStats?.kpi?.totalReports) - safeNumber(safeStats?.kpi?.resolvedReports), color: '#d97706' },
                          { name: 'En Proceso', value: Math.floor(safeNumber(safeStats?.kpi?.totalReports) * 0.1), color: '#3b82f6' }
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>

              {/* Sample Reports Table */}
              <MetricTable
                title="Reportes Recientes"
                data={[
                  { id: 'R001', tipo: 'No disponible', estado: 'resuelto', propiedad: 'Casa Lima Norte', fecha: '2025-06-20', tiempo: '2.1 días' },
                  { id: 'R002', tipo: 'Estafa', estado: 'pendiente', propiedad: 'Depto Miraflores', fecha: '2025-06-19', tiempo: '1.8 días' },
                  { id: 'R003', tipo: 'Otros', estado: 'en_proceso', propiedad: 'Terreno Arequipa', fecha: '2025-06-18', tiempo: '3.2 días' },
                  { id: 'R004', tipo: 'No disponible', estado: 'resuelto', propiedad: 'Local Comercial', fecha: '2025-06-17', tiempo: '1.5 días' },
                  { id: 'R005', tipo: 'Estafa', estado: 'pendiente', propiedad: 'Oficina San Isidro', fecha: '2025-06-16', tiempo: '4.1 días' }
                ]}
                columns={[
                  { key: 'id', header: 'ID Reporte' },
                  { key: 'tipo', header: 'Tipo' },
                  { 
                    key: 'estado', 
                    header: 'Estado',
                    render: (val) => {
                      const estado = val || 'pendiente';
                      return (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          estado === 'resuelto' ? 'bg-green-100 text-green-800' : 
                          estado === 'pendiente' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {estado === 'resuelto' ? 'Resuelto' : estado === 'pendiente' ? 'Pendiente' : 'En Proceso'}
                        </span>
                      );
                    }
                  },
                  { key: 'propiedad', header: 'Propiedad' },
                  { key: 'fecha', header: 'Fecha' },
                  { key: 'tiempo', header: 'Tiempo Transcurrido' }
                ]}
              />
            </div>
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {loading && stats && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center space-x-4">
            <Loader className="w-6 h-6 animate-spin text-blue-600" />
            <span className="text-gray-700">Actualizando datos...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default InKayrosProfessionalAnalytics;